import JsonServiceClient from 'bz-stores/lib/JsonServiceClient';
import type {
  ClearAcceptConfigResponse,
  ResponseBase,
  TakePaymentRequest
} from 'tm3-core/api.dtos';
import {
  ClearAcceptConfigRequest,
  ClearAcceptSaveCardRequest,
  ClearAcceptStartPaymentCaptureRequest,
  StartVirtualTerminalPaymentCaptureRequest
} from 'tm3-core/api.dtos';

declare global {
  interface Window {
    clearAcceptloadingPromise?: Promise<void> | null;
    HostedFields?: {
      initialise: (
        fieldToken: string,
        options: any,
        callback: (error: any, fields: any) => Promise<void>
      ) => void;
    };
  }
}

const getScript = (config: ClearAcceptConfigResponse) =>
  new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = config.scriptUrl ?? '';
    script.integrity = config.scriptSri ?? '';
    script.crossOrigin = 'anonymous';
    script.async = true;
    script.onerror = reject;

    script.onload = script.onreadystatechange = function () {
      const loadState = this.readyState;

      if (loadState && loadState !== 'loaded' && loadState !== 'complete') return;

      script.onload = script.onreadystatechange = null;

      resolve();
    };

    document.head.appendChild(script);
  });

export class ClearAcceptStore {
  client: JsonServiceClient;
  constructor() {
    this.client = new JsonServiceClient();
  }

  async load() {
    if (window.clearAcceptloadingPromise) return window.clearAcceptloadingPromise;

    window.clearAcceptloadingPromise = (async () => {
      try {
        const config = await this.loadConfigFromApi();
        await getScript(config);
      } catch (ex) {
        console.error('Failed to load ClearAccept SDK:', ex);
        window.clearAcceptloadingPromise = null;
        throw ex;
      }
    })();

    return window.clearAcceptloadingPromise;
  }
  async loadConfigFromApi(): Promise<ClearAcceptConfigResponse> {
    const res = await this.client.get(new ClearAcceptConfigRequest());
    return res.result;
  }

  async startPaymentCapture(
    amount: number | undefined,
    saveCard: boolean,
    locationId: number,
    paymentUrlCode: string,
    customerId: number,
    invoiceId: number,
    isVirtualTerminalMode: boolean,
    waitingListUrlCode: string,
    useLocationSpecificMerchantId: boolean = false,
    urlCode: string
  ): Promise<TakePaymentRequest> {
    let res: ResponseBase<TakePaymentRequest>;
    if (amount === undefined) {
      res = await this.client.post(
        new ClearAcceptSaveCardRequest({
          customerId,
          urlCode
        })
      );
    } else if (isVirtualTerminalMode) {
      res = await this.client.post(
        new StartVirtualTerminalPaymentCaptureRequest({
          amount,
          locationId,
          customerId,
          invoiceId
        })
      );
    } else {
      res = await this.client.post(
        new ClearAcceptStartPaymentCaptureRequest({
          amount,
          locationId,
          saveCard,
          paymentUrlCode,
          waitingListUrlCode,
          useLocationSpecificMerchantId
        })
      );
    }

    return res.result;
  }
}

export default ClearAcceptStore;
